import * as _ from "lodash";
import {Plex} from "@/shared/DataService/plex";
import {IPlexSummary} from "@/shared/DataService/IPlexSummary";
import {IDataService} from "@/shared/DataService/IDataService";
import AppSettings from "@/shared/Configuration/appSettings";

/**
 * Implementation of {@link IDataService} on top of the local web-storage.
 */
export default class LocalDataStore {
    private readonly projectDataPrefix: string;
    private readonly appDataPrefix: string;

    /**
     * Instantiates the {@link LocalDataStore}.
     */
    constructor() {
        this.projectDataPrefix = "_knwl_plex_";
        this.appDataPrefix = "_knwl_settings_";
    }

    /**
     * Upserts the given app settings.
     * @param settings {AppSettings} The app settings.
     * @returns {Promise<boolean>}
     */
    upsertAppSettings(settings: AppSettings): Promise<boolean> {
        return new Promise((resolve, reject) => {
            if (!(settings instanceof AppSettings)) {
                reject("Expected an AppSetting instance.");
            }
            resolve(this._save(this.appDataPrefix, settings.toJson()));
        });
    }

    /**
     * Returns the app settings.
     * @returns {Promise<AppSettings>}
     */
    getAppSettings(): Promise<AppSettings> {
        return new Promise((resolve, reject) => {
            const key = this.appDataPrefix;
            const raw = localStorage.getItem(key);
            if (_.isNil(raw)) {
                reject(`Found nil data for the app settings.`)
            }
            resolve(AppSettings.fromString(raw));
        });
    }

    /**
     * Returns the plex count.
     * @returns {Promise<number>}
     */
    plexCount(): Promise<number> {
        return new Promise((resolve, reject) => {
            let count = 0;
            for (let i = 0; i < localStorage.length; i++) {
                if (this._isProjectKey(localStorage.key(i))) {
                    count++;
                }
            }
            return resolve(count);
        });
    }

    /**
     * Remove the plex with the given id.
     * @param id {string} A plex id.
     * @returns {Promise<boolean>}
     */
    removePlex(id: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const key = this._getProjectKey(id);
            this._remove(key);
            resolve(true);
        });
    }

    /**
     * Returns the {@link Plex} with the given id.
     * @param id {string} A project id.
     * @returns {Promise<Plex>}
     */
    getPlex(id): Promise<Plex> {
        return new Promise((resolve, reject) => {
            const key = this._getProjectKey(id);
            const raw = localStorage.getItem(key);
            if (_.isNil(raw)) {
                reject(`Found nil data for id '${id}'.`)
            }
            resolve(Plex.fromString(raw));
        });
    }

    /**
     * Returns whether there are saved app settings.
     * @returns {Promise<boolean>}
     */
    appSettingsExist(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            for (let i = 0; i < localStorage.length; i++) {
                if (this._isAppSettingsKey(localStorage.key(i))) {
                    return resolve(true);
                }
            }
            return resolve(false);
        });
    }


    /**
     * Returns whether the plex with the given id is present.
     * @param id {String} A plex id.
     * @returns {Promise<boolean>}
     */
    plexIdExists(id): Promise<boolean> {
        return new Promise((resolve, reject) => {
            for (let i = 0; i < localStorage.length; i++) {
                if (localStorage.key(i) === this._getProjectKey(id)) {
                    return resolve(true);
                }
            }
            return resolve(false);
        });
    }

    /**
     * Upserts the given plex.
     * @param plex {Plex} A plex.
     * @returns {Promise<boolean>}
     */
    upsertPlex(plex: Plex): Promise<boolean> {
        return new Promise((resolve, reject) => {
            if (!(plex instanceof Plex)) {
                reject("Expected a Plex instance.");
            }
            const id = plex.id;
            resolve(this._save(this._getProjectKey(id), plex.toJson()));
        });
    }

    /**
     * Returns all plex metadata.
     * @returns {Promise<IPlexSummary[]>}
     */
    getAllPlexes(): Promise<IPlexSummary[]> {
        return new Promise((resolve, reject) => {
            const result: IPlexSummary[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                let key = localStorage.key(i);
                if (!this._isProjectKey(key)) {
                    continue;
                }
                const raw = localStorage.getItem(key);
                const id = this._getProjectId(key);

                if (_.isNil(raw)) {
                    continue;
                }
                const json = JSON.parse(raw);

                result.push({
                    id: json.id,
                    name: json.name,
                    description: json.description
                });
            }
            resolve(result);
        });
    }

    _getProjectKey(id) {
        return this.projectDataPrefix + id
    }

    _getAppKey() {
        return this.appDataPrefix
    }

    _getProjectId(key) {
        return key.substring(this.projectDataPrefix.length);
    }

    _isProjectKey(s) {
        return s.startsWith(this.projectDataPrefix);
    }

    _isAppSettingsKey(s) {
        return s === this.appDataPrefix;
    }


    _save(name: string, json: any): boolean {
        if (!_.isPlainObject(json)) {
            throw new Error("Expected plain object.")
        }
        localStorage.setItem(name, JSON.stringify(json));
        return true;
    }

    _remove(key: string) {
        localStorage.removeItem(key);
    }

}
